// socket.io-client is loaded with a DYNAMIC import, never a static one.
//
// socket.io-client + its engine.io-client dependency are ~92 KB of source in
// the startup path: this module is reachable eagerly from both app-shell.tsx
// (via use-socket.ts) and auth-store.ts, so every cold start parsed and
// compiled the whole realtime stack before first paint — even though the
// connection itself is only ever opened later, from inside a useEffect, once a
// user is authenticated. `import type` below is erased at build time, so the
// Socket type costs nothing.
//
// getSocket() is therefore ASYNC. Every caller already invoked it from an
// effect, and useSocket() already models "no socket yet" with useState(null),
// so awaiting the import fits the shape the callers already had. Callers MUST
// guard their .then() with a cancelled flag — see use-socket.ts — otherwise a
// socket that resolves after unmount attaches listeners to a dead component.
import type { Socket } from "socket.io-client";

// Module-level singleton. `_socket` is the live connection; `_identity`
// is the userId + token combo it was opened with. If the identity
// changes (impersonation handoff, fresh login on the same tab), we tear
// down the old socket and start a new one — otherwise the server would
// keep sending events to the wrong personal room.
let _socket: Socket | null = null;
let _identity: { userId: string; token: string } | null = null;
// In-flight connect. Without this, two hooks mounting in the same tick would
// both miss the `_socket` check, both await the import, and both call io() —
// opening two connections and leaving one orphaned. Concurrent callers now
// coalesce onto a single connect, the same way api/client.ts coalesces
// refreshes.
let _connecting: Promise<Socket> | null = null;
// Bumped by anything that invalidates an in-flight connect: an identity change
// or an explicit disconnectSocket(). The connect closure captures the epoch
// before it awaits the dynamic import and re-checks it after; if it changed,
// the connect is abandoned instead of assigning itself to `_socket`.
//
// Without this, logging out DURING the import window left a zombie: logout ran
// disconnectSocket() (which nulls `_socket`), the import then resolved, saw
// `_socket === null`, happily opened a connection for the user who had just
// signed out, and stored it in the singleton. Same hazard on an impersonation
// handoff, where the old identity's connect would clobber the new one.
let _epoch = 0;

/** Cached module namespace so we only pay the dynamic import once. */
let _ioModule: typeof import("socket.io-client") | null = null;

async function loadIO(): Promise<typeof import("socket.io-client")> {
  if (!_ioModule) {
    _ioModule = await import("socket.io-client");
  }
  return _ioModule;
}

function socketOrigin(): string | undefined {
  const apiUrl = import.meta.env["VITE_API_URL"] as string | undefined;
  if (apiUrl?.startsWith("http://") || apiUrl?.startsWith("https://")) {
    return apiUrl.replace(/\/+$/, "");
  }
  return undefined;
}

export async function getSocket(userId: string, token: string): Promise<Socket> {
  // Identity changed (different user / refreshed token) — kill the old
  // connection so the next one carries the right auth + joins the
  // right personal room. Without this guard a stale socket would keep
  // receiving events for the previous user after logout / handoff.
  //
  // Done synchronously, BEFORE any await, so a stale socket can never survive
  // into the window where we're loading the module.
  if (_socket && _identity && (_identity.userId !== userId || _identity.token !== token)) {
    try {
      _socket.disconnect();
    } catch {
      // Best-effort cleanup; ignore any teardown errors.
    }
    _socket = null;
    _identity = null;
    // Any connect still in flight was started for the PREVIOUS identity.
    // Invalidate it (so it abandons itself on resume) and drop our reference so
    // the next caller starts a fresh one rather than coalescing onto the wrong
    // user's connection.
    _epoch += 1;
    _connecting = null;
  }

  // Reuse any existing socket for this identity — deliberately WITHOUT
  // checking `.connected`.
  //
  // The old code was `if (!_socket || !_socket.connected) { _socket = io(...) }`,
  // which had a latent duplicate-connection bug: io() returns immediately with
  // connected === false (the handshake is still in flight), so two hooks
  // mounting in the same tick both saw "not connected" and both called io() —
  // the second overwrote `_socket` and orphaned the first, which then kept
  // polling forever with nothing listening to it. Reuse is the correct
  // singleton semantics here anyway: this socket has no reconnectionAttempts
  // cap (see below), so a momentarily-disconnected socket reconnects on its
  // own and does not need replacing. Explicit teardown still goes through
  // disconnectSocket(), which nulls `_socket` and forces a fresh connect.
  if (_socket) return _socket;
  // Someone else is already connecting for this identity — join them instead
  // of opening a second socket.
  if (_connecting) return _connecting;

  const mine: Promise<Socket> = (async (): Promise<Socket> => {
    const myEpoch = _epoch;
    const { io: connectIO } = await loadIO();

    // Superseded while the module was loading — a logout or an identity change
    // happened. Do NOT open a connection: it would belong to nobody and would
    // install itself as the singleton for whoever comes next.
    if (myEpoch !== _epoch) {
      throw new Error("socket connect superseded (logout or identity change)");
    }

    // Another caller may have finished connecting while we were loading.
    if (_socket) return _socket;

    const socket = connectIO(socketOrigin(), {
      path: "/socket.io",
      auth: { userId, token },
      // Defect #5 — the staging ingress doesn't always forward the
      // WebSocket upgrade handshake, which produced a console-wide
      // "WebSocket connection to wss://…/socket.io/ failed" error on
      // every page load. We start with HTTP long-polling (always
      // works through any L7 proxy) and let socket.io upgrade to
      // WebSocket transparently when it can.
      //
      // Functionally identical for the user; just quieter in the
      // console and one fewer "is staging broken?" Slack ping.
      transports: ["polling", "websocket"],
      // No `reconnectionAttempts` cap — socket.io's default is to retry
      // forever with backoff. A previous cap of 5 (~20-30s of backoff)
      // meant any real-world blip (WiFi roam, laptop sleep, mobile
      // network handover, a brief LB hiccup) killed this shared
      // singleton PERMANENTLY until the user hard-refreshed the tab —
      // that was the actual cause behind "cross-dashboard updates stop
      // working, but work again after a while / retry." Unlimited
      // retries with a capped delay is the right trade: worst case is a
      // quiet retry every 10s, not a silently dead connection.
      reconnectionDelayMax: 10_000,
    });

    _socket = socket;
    _identity = { userId, token };
    return socket;
  })();
  _connecting = mine;

  try {
    return await mine;
  } finally {
    // Clear so a failed import/connect doesn't wedge every future caller onto a
    // permanently rejected promise — but ONLY if `_connecting` is still OURS.
    // A logout mid-connect nulls `_connecting` and a later caller may already
    // have installed a newer one; blindly nulling here would clobber that and
    // let the next caller open a duplicate socket alongside it.
    if (_connecting === mine) _connecting = null;
  }
}

/** Returns true if a live socket connection is currently open. */
export function isSocketConnected(): boolean {
  return !!(_socket?.connected);
}

// Explicit teardown. Call from logout — NOT from per-component
// useEffect cleanup. The socket is process-wide (multiple hooks
// subscribe to it), so per-component teardown kills events for every
// other subscriber on the page.
export function disconnectSocket(): void {
  _socket?.disconnect();
  _socket = null;
  _identity = null;
  // Invalidate + forget any connect still in flight, so a socket.io-client
  // import that resolves after logout abandons itself instead of opening a
  // connection for the user who just signed out.
  _epoch += 1;
  _connecting = null;
}
